<script>
    import { selectedItem } from "../../store/store.js";
    import General from "./General.svelte";
    import MaintenancePlan from "./MaintenancePlan.svelte";
    import Economy from "./Economy.svelte";
    import Building from "./Building.svelte";
    import Energy from "./Energy.svelte";
    import Stadgar from "./Stadgar.svelte";

    function scrollToSection(sectionId) {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    async function handleShare() {
        const url = window.location.href;
        const title = $selectedItem?.brf_name || "BRF Rapport";
        
        if (navigator.share) {
            try {
                await navigator.share({
                    title: title,
                    text: `Betygsrapport för ${title}`,
                    url: url
                });
            } catch (err) {
                if (err.name !== "AbortError") {
                    copyToClipboard(url);
                }
            }
        } else {
            copyToClipboard(url);
        }
    }

    function copyToClipboard(text) {
        navigator.clipboard.writeText(text).then(() => {
            alert("Länk kopierad till urklipp!");
        }).catch(() => {
            const textarea = document.createElement("textarea");
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
            alert("Länk kopierad till urklipp!");
        });
    }

    function handlePrint() {
        window.print();
    }

    // Check which sections have data
    $: hasEnergy = $selectedItem?.energy_declaration_id != null;
    $: hasStadgar = $selectedItem?.stadgar_id != null;
</script>

{#if $selectedItem}
    <div class="rating-container">
        <div class="header-container">
            <div class="top-header">
                <div class="logo-brand">
                    <img
                        src="/bo-emblem-black.png"
                        alt="boanalys logo"
                        class="header-logo"
                    />
                    <h1>boanalys</h1>
                </div>
                <div class="header-title">
                    <h1>{$selectedItem.brf_name}</h1>
                </div>
            </div>
            <div class="header-divider"></div>
            <header class="main-header">
                <nav class="section-nav">
                    <button
                        class="nav-btn"
                        on:click={() => scrollToSection("general")}
                        >Översikt</button
                    >
                    <button
                        class="nav-btn"
                        on:click={() => scrollToSection("maintenance")}
                        >Underhållsplan</button
                    >
                    <button
                        class="nav-btn"
                        on:click={() => scrollToSection("economy")}
                        >Ekonomi</button
                    >
                    <button
                        class="nav-btn"
                        on:click={() => scrollToSection("building")}
                        >Fastighet</button
                    >
                    {#if hasEnergy}
                        <button
                            class="nav-btn"
                            on:click={() => scrollToSection("energy")}
                            >Energi</button
                        >
                    {/if}
                    {#if hasStadgar}
                        <button
                            class="nav-btn"
                            on:click={() => scrollToSection("stadgar")}
                            >Stadgar</button
                        >
                    {/if}
                </nav>
                <div class="actions">
                    <button class="action-btn" on:click={handleShare}
                        ><i class="fas fa-share-alt"></i></button
                    >
                    <button class="action-btn" on:click={handlePrint}
                        ><i class="fas fa-print"></i></button
                    >
                </div>
            </header>
        </div>
        <div class="dashboard-container">
            <main class="main-content">
                <div class="content-wrapper">
                    <div id="general">
                        <General />
                    </div>

                    <div class="content-grid">
                        <div id="maintenance" class="maintenance-wrapper">
                            <MaintenancePlan />
                        </div>

                        <div id="economy" class="economy-wrapper">
                            <Economy />
                        </div>

                        <div id="building" class="building-wrapper">
                            <Building />
                        </div>

                        <div id="energy" class="energy-wrapper">
                            <Energy />
                        </div>

                        <div id="stadgar" class="stadgar-wrapper">
                            <Stadgar />
                        </div>
                    </div>
                </div>
            </main>
        </div>
    </div>
{/if}

<style>
    @font-face {
        font-family: "MuseoModerna";
        src: url("/MuseoModerno.ttf") format("truetype");
        font-weight: normal;
        font-style: normal;
    }

    .rating-container {
        font-family: var(--test-font-family);
        width: 100%;
        max-width: 100%;
        margin: 0 auto;
        box-sizing: border-box;
    }

    .rating-container :global(h1) {
        font-family: "MuseoModerna", sans-serif;
        font-size: 2rem;
        font-weight: 600;
        color: #333;
        margin: 0 0 0 0;
        line-height: 1.2;
        letter-spacing: -0.02em;
    }

    .rating-container :global(h2) {
        font-family: var(--test-font-family);
        font-size: 1.5rem;
        font-weight: 600;
        color: #333;
        margin: 0 0 0 0;
        line-height: 1.3;
    }

    .rating-container :global(h3) {
        font-family: var(--test-font-family);
        font-size: 1.25rem;
        font-weight: 600;
        color: #495057;
        margin: 0 0 0 0;
        line-height: 1.4;
    }

    .rating-container :global(p) {
        font-family: var(--test-font-family);
        font-size: 1rem;
        font-weight: 400;
        color: #555;
        line-height: 1.6;
        margin: 0 0 0 0;
    }

    .header-container {
        margin: 1rem auto;
        max-width: 1200px;
        background: white;
        backdrop-filter: blur(30px) saturate(120%);
        -webkit-backdrop-filter: blur(30px) saturate(120%);
        border: none;
        border-radius: 24px;
        overflow: hidden;
        position: relative;
        z-index: 1;
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.3),
            inset 0 -1px 0 rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.15);
    }

    .top-header {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem 2rem;
        background: transparent;
    }

    .logo-brand {
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }

    .header-divider {
        height: 1px;
        background: linear-gradient(
            to right,
            transparent 0%,
            rgba(255, 255, 255, 0.3) 20%,
            rgba(255, 255, 255, 0.3) 80%,
            transparent 100%
        );
        margin: 0 1rem;
    }

    .header-logo {
        width: 50px;
        height: auto;
    }

    .header-title {
        font-size: 1.25rem;
        font-weight: 500;
        color: #333;
        margin-left: auto;
    }

    .dashboard-container {
        display: flex;
        flex-direction: column;
        min-height: 100vh;
        font-family: var(--test-font-family);
        position: relative;
        width: 100%;
        max-width: 100%;
    }

    .main-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 2rem;
        background: transparent;
        flex-shrink: 0;
        position: relative;
        z-index: 1;
    }

    .section-nav {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
        justify-content: center;
    }

    .nav-btn {
        background: rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(15px) saturate(120%);
        -webkit-backdrop-filter: blur(15px) saturate(120%);
        border: none;
        border-radius: 12px;
        padding: 0.5rem 1rem;
        cursor: pointer;
        font-size: 0.9rem;
        font-weight: 500;
        color: #495057;
        transition: all 0.3s ease;
        white-space: nowrap;
        box-shadow:
            0 2px 8px rgba(0, 0, 0, 0.05),
            0 0 0 1px rgba(255, 255, 255, 0.2);
    }

    .nav-btn:hover {
        background: rgba(255, 255, 255, 0.35);
        color: #4361ee;
        transform: translateY(-2px);
        box-shadow:
            0 4px 12px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(67, 97, 238, 0.3);
    }

    .main-content {
        padding: 2rem 0;
        background: transparent;
        position: relative;
        z-index: 1;
        width: 100%;
    }

    .content-wrapper {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 2rem;
        position: relative;
        z-index: 1;
        width: 100%;
        box-sizing: border-box;
    }

    .content-wrapper > div[id] {
        scroll-margin-top: 1rem;
    }

    .actions {
        display: flex;
        gap: 0.5rem;
    }

    .actions :global(.action-btn) {
        background: rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(15px) saturate(120%);
        -webkit-backdrop-filter: blur(15px) saturate(120%);
        border: none;
        border-radius: 10px;
        width: 40px;
        height: 40px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s ease;
        box-shadow:
            0 2px 8px rgba(0, 0, 0, 0.05),
            0 0 0 1px rgba(255, 255, 255, 0.2);
    }

    .actions :global(.action-btn:hover) {
        background: rgba(255, 255, 255, 0.35);
        color: #4361ee;
        transform: translateY(-2px);
        box-shadow:
            0 4px 12px rgba(0, 0, 0, 0.08),
            0 0 0 1px rgba(67, 97, 238, 0.3);
    }

    .content-grid {
        display: grid;
        gap: 2rem;
        grid-template-areas:
            "maintenance maintenance"
            "economy economy"
            "building building"
            "energy energy"
            "stadgar stadgar";
    }

    .maintenance-wrapper {
        grid-area: maintenance;
    }

    .economy-wrapper {
        grid-area: economy;
    }

    .building-wrapper {
        grid-area: building;
    }

    .energy-wrapper {
        grid-area: energy;
    }

    .stadgar-wrapper {
        grid-area: stadgar;
    }

    .rating-container :global(.card) {
        background: white;
        backdrop-filter: blur(25px) saturate(120%);
        -webkit-backdrop-filter: blur(25px) saturate(120%);
        border-radius: 20px;
        padding: 1.5rem;
        border: none;
        box-shadow:
            0 8px 32px rgba(0, 0, 0, 0.06),
            0 0 0 1px rgba(255, 255, 255, 0.15);
    }

    .rating-container :global(.stat-card) {
        background: rgba(255, 255, 255, 0.12);
        backdrop-filter: blur(20px) saturate(120%);
        -webkit-backdrop-filter: blur(20px) saturate(120%);
        padding: 1.5rem;
        border-radius: 16px;
        border: none;
        box-shadow:
            0 4px 16px rgba(0, 0, 0, 0.04),
            0 0 0 1px rgba(255, 255, 255, 0.15);
        height: 120px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        flex: 0 0 calc(15% - 1.125rem);
        min-width: 100px;
        transition: all 0.3s ease;
    }

    .rating-container :global(.stat-card:hover) {
        background: rgba(255, 255, 255, 0.2);
        transform: translateY(-2px);
        box-shadow:
            0 6px 20px rgba(0, 0, 0, 0.06),
            0 0 0 1px rgba(255, 255, 255, 0.2);
    }

    .rating-container :global(.stat-card h6) {
        text-transform: capitalize;
        margin: 0 0 0.5rem 0;
        color: #666;
        font-size: 0.9rem;
    }

    .rating-container :global(.stat-card .value) {
        font-size: 2rem;
        font-weight: 600;
        margin: 0;
        color: #222;
    }

    .rating-container :global(.stat-card .unit) {
        margin: 0;
        color: #888;
    }

    .rating-container :global(.image-container) {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 300px;
        background: rgba(255, 255, 255, 0.12);
        backdrop-filter: blur(20px) saturate(120%);
        -webkit-backdrop-filter: blur(20px) saturate(120%);
        border-radius: 16px;
        border: none;
        padding: 1rem;
        box-shadow:
            0 4px 16px rgba(0, 0, 0, 0.04),
            0 0 0 1px rgba(255, 255, 255, 0.15);
    }

    .rating-container :global(.image-container img) {
        max-width: 400px;
        height: auto;
        border-radius: 12px;
        display: block;
    }

    .rating-container :global(.image-upload-wrapper) {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        align-items: center;
        padding: 1rem;
        min-height: 75px;
        justify-content: center;
        width: 100%;
    }

    .rating-container :global(.file-input) {
        display: none;
    }

    .rating-container :global(.upload-button) {
        background: rgba(67, 97, 238, 0.8);
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
        color: white;
        border: 1px solid rgba(255, 255, 255, 0.3);
        padding: 12px 25px;
        border-radius: 12px;
        cursor: pointer;
        font-weight: 600;
        transition: all 0.3s ease;
        font-size: 1rem;
        box-shadow: 0 4px 16px rgba(67, 97, 238, 0.3);
    }

    .rating-container :global(.upload-button:hover:not(:disabled)) {
        background: rgba(67, 97, 238, 0.9);
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(67, 97, 238, 0.4);
    }

    .rating-container :global(.upload-button:disabled) {
        opacity: 0.6;
        cursor: not-allowed;
    }

    .rating-container :global(.error-message) {
        color: #dc3545;
        margin-top: 0.5rem;
        text-align: center;
    }

    .rating-container :global(.section-title) {
        font-size: 1rem;
        color: #495057;
        margin-bottom: 12px;
        padding-bottom: 6px;
        border-bottom: 1px solid #e9ecef;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .rating-container :global(.section-title i) {
        color: #4361ee;
    }

    @media (max-width: 1200px) {
        .content-grid {
            grid-template-columns: 1fr;
            grid-template-areas:
                "maintenance"
                "economy"
                "building"
                "energy"
                "stadgar";
        }
    }

    @media (max-width: 992px) {
        .header-title {
            font-size: 1.1rem;
            margin-left: 0;
        }

        .main-header {
            flex-direction: column;
            gap: 1rem;
            align-items: flex-start;
        }

        .section-nav {
            width: 100%;
            justify-content: flex-start;
        }

        .actions {
            align-self: flex-end;
        }

        .rating-container :global(.stat-card) {
            flex: 0 0 calc(50% - 0.75rem);
        }
    }

    @media (max-width: 576px) {
        .header-container {
            margin: 0.5rem auto;
            border-radius: 20px;
        }

        .top-header {
            padding: 1rem;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
        }

        .logo-brand {
            justify-content: center;
        }

        .header-title {
            font-size: 1rem;
            margin-left: 0;
            text-align: center;
        }

        .main-header {
            padding: 1rem;
        }

        .header-divider {
            margin: 0 1rem;
        }

        .main-content {
            padding: 1rem 0;
        }

        .content-wrapper {
            padding: 0 1rem;
        }

        .rating-container :global(.stat-card) {
            flex: 0 0 100%;
        }
    }
</style>
